import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { 
  Award,
  Download,
  Calendar,
  CheckCircle2,
  Trophy
} from "lucide-react";

export default function CertificatesPage() {
  const [sops, setSops] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sopsRes, progressRes] = await Promise.all([
        apiClient.get("/sops"),
        apiClient.get("/progress"),
      ]);
      setSops(sopsRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load certificates");
    } finally {
      setLoading(false);
    }
  };

  const getCompletedModules = () => {
    return progress
      .filter((p) => p.completed && p.quiz_passed)
      .map((p) => {
        const sop = sops.find((s) => s.id === p.sop_id);
        return { ...p, sop };
      })
      .filter((p) => p.sop);
  };

  const handleDownloadCertificate = async (sopId, sopTitle) => {
    try {
      const token = localStorage.getItem("token");
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/certificate/${sopId}/download`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Download failed");
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RoyalDornoch_Certificate_${sopTitle.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success("Certificate downloaded!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download certificate");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    } catch {
      return "N/A";
    }
  };

  const completedModules = getCompletedModules();
  const totalModules = sops.length;

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto space-y-6">
          <div className="skeleton h-12 w-64"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton h-48"></div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-8 animate-fade-in-up">
        {/* Header */}
        <div>
          <h1 className="text-2xl lg:text-3xl text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
            My Certificates
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">
            Your achievements and training certifications at Royal Dornoch.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="stat-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))]">
                  Certificates Earned
                </p>
                <p className="text-4xl font-semibold text-[var(--rd-green)] mt-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  {completedModules.length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[var(--rd-gold)]/10 flex items-center justify-center">
                <Award className="w-6 h-6 text-[var(--rd-gold)]" />
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))]">
                  Total Modules
                </p>
                <p className="text-4xl font-semibold text-[var(--rd-green)] mt-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  {totalModules}
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[var(--rd-green)]/10 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-[var(--rd-green)]" />
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))]">
                  Completion Rate
                </p>
                <p className="text-4xl font-semibold text-[var(--rd-green)] mt-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  {totalModules > 0 ? Math.round((completedModules.length / totalModules) * 100) : 0}%
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-green-50 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Certificates Grid */}
        {completedModules.length === 0 ? (
          <Card className="border-[hsl(var(--border))]">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center mx-auto mb-6">
                <Award className="w-10 h-10 text-[hsl(var(--muted-foreground))]" />
              </div>
              <h2 className="text-xl text-[var(--rd-green)] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                No Certificates Yet
              </h2>
              <p className="text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
                Complete training modules and pass the assessments to earn your Royal Dornoch certificates.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {completedModules.map((item, index) => (
              <Card 
                key={item.sop_id} 
                className={`border-[hsl(var(--border))] overflow-hidden hover-lift stagger-${(index % 4) + 1} animate-fade-in-up`}
              >
                <div className="h-2 bg-[var(--rd-gold)]" />
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-sm bg-[var(--rd-gold)]/10 flex items-center justify-center flex-shrink-0">
                      <Award className="w-7 h-7 text-[var(--rd-gold)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold tracking-wider uppercase text-[var(--rd-gold)] mb-1">
                        Certificate of Completion
                      </p>
                      <h3 className="text-lg text-[var(--rd-green)] mb-2 truncate" style={{ fontFamily: "'Cinzel', serif" }}>
                        {item.sop?.title}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-[hsl(var(--muted-foreground))]">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(item.completed_at)}
                        </span>
                        <span className="font-medium text-green-600">
                          Score: {item.quiz_score}%
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]">
                    <Button
                      onClick={() => handleDownloadCertificate(item.sop_id, item.sop?.title)}
                      variant="outline"
                      className="w-full border-[var(--rd-gold)] text-[var(--rd-gold)] hover:bg-[var(--rd-gold)] hover:text-white"
                      data-testid={`download-cert-${item.sop_id}`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Certificate
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
